import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Play, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { handleError } from "@/lib/errorHandler";

interface AutomationTesterProps {
  automationId: string;
}

export const AutomationTester = ({ automationId }: AutomationTesterProps) => {
  const [testing, setTesting] = useState(false);

  const testAutomation = async () => {
    setTesting(true);
    try {
      // Create a test run
      const { data: run, error: runError } = await supabase
        .from("automation_runs")
        .insert([{
          automation_id: automationId,
          status: "running",
          logs: [{ message: "Test run initiated", timestamp: new Date().toISOString() }]
        }])
        .select()
        .single();

      if (runError) throw runError;

      // Simulate automation execution
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Update run to success
      const { error: updateError } = await supabase
        .from("automation_runs")
        .update({
          status: "success",
          finished_at: new Date().toISOString(),
          logs: [
            { message: "Test run initiated", timestamp: new Date().toISOString() },
            { message: "Trigger validated", timestamp: new Date().toISOString() },
            { message: "Actions executed successfully", timestamp: new Date().toISOString() }
          ]
        })
        .eq("id", run.id);

      if (updateError) throw updateError;

      toast.success("Test run completed successfully!");
    } catch (error: any) {
      handleError(error, "Test run failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Test Automation</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Run a test to verify your automation works correctly.
        </p>
        <Button onClick={testAutomation} disabled={testing}>
          {testing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Running Test...
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-2" />
              Run Test
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
